import React from 'react';
import PropTypes from 'prop-types';
import secureLog from './secureLogger';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { config, analytics } = props;
  
  secureLog.debug('IncludeScripts initialized', { 
    hasAnalytics: !!analytics,
    hasConfig: !!config
  });

  // Don't render anything if no analytics config exists
  if (!analytics) {
    return null;
  }

  const { fbPixelId, googleAnalyticsId, googleTagManagerId } = analytics;
  const analyticsLibraries = [];

  // Add Facebook Pixel if ID exists
  if (fbPixelId) {
    secureLog.debug('Facebook Pixel enabled');
    
    analyticsLibraries.push(
      <script key="fb-pixel">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${fbPixelId}');
          fbq('track', 'PageView');
        `}
      </script>
    );
    
    analyticsLibraries.push(
      <noscript key="fb-pixel-noscript">
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${fbPixelId}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    );
  }

  // Add Google Analytics if ID exists
  if (googleAnalyticsId) {
    secureLog.debug('Google Analytics enabled');
    
    analyticsLibraries.push(
      <script key="ga" async src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`} />
    );
    
    analyticsLibraries.push(
      <script key="ga-config">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${googleAnalyticsId}');
        `}
      </script>
    );
  }

  // Add Google Tag Manager if ID exists
  if (googleTagManagerId) {
    secureLog.debug('Google Tag Manager enabled');
    
    analyticsLibraries.push(
      <script key="gtm">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${googleTagManagerId}');
        `}
      </script>
    );
    
    analyticsLibraries.push(
      <noscript key="gtm-noscript">
        <iframe
          title="gtm-noscript"
          src={`https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    );
  }

  return <>{analyticsLibraries}</>;
};

IncludeScripts.defaultProps = {
  config: null,
  analytics: null,
};

IncludeScripts.propTypes = {
  config: PropTypes.object,
  analytics: PropTypes.shape({
    fbPixelId: PropTypes.string,
    googleAnalyticsId: PropTypes.string,
    googleTagManagerId: PropTypes.string,
  }),
};